import type { OwnerTerritory } from "../../domain/owner-territories";
import type { TerritoryPreview } from "./useTerritoryPreviews";
import { useI18n } from "../../i18n";
import { cn } from "../cn";
import { TerritoryIdentity } from "./TerritoryIdentity";

/**
 * TerritoryDetail — read-only management summary for one selected territory.
 * Authoritative/derived fields only, plus the persistent Identity's real name
 * when present. No fake persisted ID, no content authority (no Used/Available
 * GP), and no non-functional/disabled controls (Claudio §9).
 */
export function TerritoryDetail({
  ordinal,
  territory,
  preview,
}: {
  ordinal: number;
  territory: OwnerTerritory;
  preview?: TerritoryPreview;
}) {
  const { t } = useI18n();
  const fallback = t("dashboard.territory.fallback", { ordinal: String(ordinal).padStart(2, "0") });
  const name = preview?.displayName?.trim() ? preview.displayName : fallback;
  const { bounds } = territory;
  return (
    <aside
      data-testid="territory-detail"
      className="rounded-lg bg-panel border border-border-soft p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2
            data-testid="td-name"
            title={name}
            className="m-0 truncate font-display text-[16px] font-semibold tracking-[-0.01em] text-text-primary"
          >
            {name}
          </h2>
          {preview?.displayName?.trim() && (
            <span className="mt-0.5 block font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted">
              {fallback}
            </span>
          )}
        </div>
        <span
          data-testid="td-publishability"
          data-publishable={territory.publishable}
          data-published={Boolean(preview?.published)}
          className={cn(
            "shrink-0 inline-flex items-center h-5 px-2 rounded-sm font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap",
            preview?.published
              ? "text-neon-teal border border-border-accent bg-neon-teal-soft"
              : territory.publishable
              ? "text-neon-teal border border-border-accent"
              : "text-text-muted border border-border-soft",
          )}
        >
          {preview?.published
            ? t("dashboard.publishability.published")
            : territory.publishable
            ? t("dashboard.publishability.publishable")
            : t("dashboard.publishability.notPublishable")}
        </span>
      </div>

      <dl className="mt-5 space-y-4">
        <Row label={t("dashboard.detail.territoryArea")}>
          <span data-testid="td-area">{territory.area.toLocaleString()} u²</span>
        </Row>
        <Row label={t("dashboard.detail.gpCapacity")}>
          <span data-testid="td-gp" className="text-neon-teal">
            {territory.capacity} GP
          </span>
        </Row>
        <Row label={t("dashboard.detail.propertyUnits")}>
          <span data-testid="td-units">{territory.unitCount}</span>
        </Row>
        <Row label={t("dashboard.detail.underlyingUnitIds")}>
          <span data-testid="td-unit-ids" className="break-words">
            {territory.unitIds.join(", ")}
          </span>
        </Row>
        <Row label={t("dashboard.detail.location")}>
          <span data-testid="td-bounds">
            ({bounds.minX}, {bounds.minY}) → ({bounds.maxX}, {bounds.maxY})
          </span>
        </Row>
      </dl>

      {!territory.publishable && (
        <p className="mt-5 text-[12px] text-text-muted">
          {t("dashboard.detail.minNote")}
        </p>
      )}

      <TerritoryIdentity territory={territory} />
    </aside>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[10px] uppercase tracking-[0.16em] text-text-muted font-mono shrink-0">
        {label}
      </dt>
      <dd className="m-0 font-mono text-[13px] text-text-primary text-right">
        {children}
      </dd>
    </div>
  );
}
